import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Loader2, AlertCircle, Download } from 'lucide-react';
import { userService } from '../../../_services';
import { exportToStyledExcel } from '../../../utils/exportToExcel';

/**
 * Commission Payout Report Dialog
 * Modernized modal for downloading commission payout reports
 * Downloads styled Excel with alternating row colors
 */
function CommissionPayoutDialog({ open, onOpenChange, title }) {
    const [selectedMonth, setSelectedMonth] = useState('');
    const [monthData, setMonthData] = useState([]);
    const [loadingMonths, setLoadingMonths] = useState(false);
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState(false);

    // Fetch month list when dialog opens using FetchPayoutReportMonth (matching dev-v3)
    useEffect(() => {
        if (!open) {
            setError('');
            setDownloading(false);
            return;
        }

        const fetchMonths = async () => {
            setLoadingMonths(true);
            try {
                const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
                const userId = Usermeta_Data.split(':')[0];
                const userName = Usermeta_Data.split(':')[1];

                const apidata = await userService.FetchPayoutReportMonth(userId, userName);

                if (apidata && apidata.length > 0) {
                    setMonthData(apidata);
                    setSelectedMonth(apidata[0].date_Month);
                } else {
                    setMonthData([]);
                }
            } catch (err) {
                console.error('Error fetching commission payout months:', err);
                setMonthData([]);
            } finally {
                setLoadingMonths(false);
            }
        };

        fetchMonths();
    }, [open]);

    // Download report handler
    const handleDownload = async () => {
        if (!selectedMonth) {
            setError('Please select a month');
            return;
        }

        setDownloading(true);
        setError('');

        try {
            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            const userId = Usermeta_Data.split(':')[0];
            const userName = Usermeta_Data.split(':')[1];

            // Pass selectedMonth as 3rd arg (matching dev-v3 and user.service.js signature)
            const response = await userService.FetchPayoutReportLogs(userId, userName, selectedMonth);

            if (!response || response.length === 0) {
                setError('No data available for the selected month');
                setDownloading(false);
                return;
            }

            // Transform data for Excel with proper column names (matching dev-v3)
            const tableData = response.map(item => ({
                "RecipientCompanyAssignedUniqueKey(primary group)": item.primary_Group,
                "Additionalgroups": item.additionalgroups,
                "FirstName": item.firstName,
                "LastName": item.lastName,
                "RecipientNotificationEmailAddress": item.emailAddress,
                "Earned Commission": item.earned_Commission,
                "Commission for Sub Distributor": item.commission_for_SubDist,
                "Commission Due to Distributor": item.commission_Due_to_Distributor,
                "Actual Earned Commission": item.actual_Earned,
                "Avalara W9 Tax Status": item.avalaraW9Tax_Status,
                "Wise Account Status": item.wiseAccount_Status,
                "PQ Invite Status": item.pqInvite_Status,
                "PQ Registration Status": item.pqRegistration_Status,
            }));

            // Generate filename from month (matching dev-v3 pattern)
            const monthFormatted = selectedMonth.split('T')[0].trim().split(',').join('_');
            const filename = `NuLifeSpanRx_Commission_Payout_Report_${monthFormatted}`;

            // Export with styling (alternating row colors, styled header)
            await exportToStyledExcel(tableData, filename, {
                headerColor: '4F81BD',
                oddRowColor: 'DBE5F1',
                evenRowColor: 'B8CCE4',
                sheetName: 'Commission Payout Report'
            });

        } catch (err) {
            console.error('Error downloading commission payout report:', err);
            setError('Failed to download report. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold text-primary">
                        {title} Report
                    </DialogTitle>
                    <DialogDescription>
                        Select a month to download the commission payout report as Excel
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Month Selector */}
                    <div className="space-y-2">
                        <Label htmlFor="month-select">Choose Date</Label>
                        {loadingMonths ? (
                            <div className="flex items-center gap-2 text-primary/70">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Loading months...</span>
                            </div>
                        ) : monthData && monthData.length > 0 ? (
                            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                                <SelectTrigger id="month-select" className="w-full">
                                    <SelectValue placeholder="Select Month" />
                                </SelectTrigger>
                                <SelectContent>
                                    {monthData.map((item, index) => (
                                        <SelectItem key={index} value={item.date_Month}>
                                            {item.date_Month}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : (
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>
                                    No months available. Please try again later.
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>

                    {/* Error Message */}
                    {error && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={downloading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDownload}
                        disabled={downloading || loadingMonths || !selectedMonth}
                        variant="secondary"
                    >
                        {downloading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Downloading...
                            </>
                        ) : (
                            <>
                                <Download className="mr-2 h-4 w-4" />
                                Download
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CommissionPayoutDialog;
